/**
 * @file routes/(public)/me/offline.tsx
 * @description 離線現場入口頁（公開、極簡、零後端依賴）。
 *
 * 為何放在 `(public)` 而非 `_app`：
 * - 本頁是稽核員在現場「斷網、可能剛 hard refresh / 重開 PWA」時的落地頁，
 *   必須能在**無法請求後端**的情況下打開，因此不能走 `_app` 的認證守衛
 *   （`beforeLoad` 會打 `/api/auth/me`）也不套 `AppShell`（側邊欄 / 頭部 / 用戶信息）。
 * - URL 仍為 `/me/offline`（`(public)` 為 pathless 分組），所有既有跳轉
 *   （`AppShell` 斷網 replace、Header 下拉、AuditorWorkbench、任務頁「離線任務」按鈕）
 *   無需改動目標路徑。
 *
 * 內容：
 * - 只渲染「已下載離線任務包」列表（`OfflineTaskPackList`，純讀本地 IndexedDB）。
 * - 頂部自帶極簡標題欄 + 線上 / 離線標籤；在線時提供「返回完整後台」。
 * - 不顯示設備 / 同步 / 備份等管理卡片（那些需要登入 + 後端，已遷至 `/me/storage`）。
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { ListChecks, LogOut, Wifi, WifiOff } from 'lucide-react';
import { OfflineTaskPackList } from '@/components/notifications/OfflineTaskPackList';
import { setOfflineFocusIntent } from '@/hooks/useOfflineFocusMode';

/** 路由定義（公開，無認證守衛、無 AppShell） */
export const Route = createFileRoute('/(public)/me/offline')({ component: OfflineFieldEntryPage });

/**
 * 離線現場入口頁。
 * 自帶極簡殼層，不依賴 BaseLayout / AppShell。
 */
function OfflineFieldEntryPage() {
  // 僅本地判斷網絡狀態用於展示，不發任何請求
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /** 返回完整後台（清除離線專注意圖），僅在線時提供 */
  const exitFocusMode = () => {
    setOfflineFocusIntent(false);
    window.location.assign('/audit-tasks');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        padding: '16px clamp(16px, 4vw, 40px)',
        boxSizing: 'border-box',
      }}
    >
      {/* 極簡標題欄：標題 + 線上/離線標籤 +（在線時）返回完整後台 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <Typography.Title
          level={3}
          style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <ListChecks size={24} />
          離線任務
        </Typography.Title>
        <Space wrap>
          {/* 用 inline-flex 包住圖標+文字：Tailwind preflight 把 svg 設成 display:block，
              直接用 antd Tag 的 icon prop 會讓裸 lucide svg 佔整行、文字被擠到下一行。 */}
          <Tag color={online ? 'green' : 'orange'}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {online ? <Wifi size={12} /> : <WifiOff size={12} />}
              {online ? '線上' : '離線'}
            </span>
          </Tag>
          {online && (
            <Button size="small" icon={<LogOut size={14} />} onClick={exitFocusMode}>
              返回完整後台
            </Button>
          )}
        </Space>
      </div>

      <OfflineTaskPackList />
    </div>
  );
}
